/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/05/22
 */

import { useAxios } from '@/components/AxiosContext';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { cloneDeep, set } from 'lodash';
import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { isNullish } from '@/wpt-lib/utils/isNullish';

export interface FormLabelsDto {
    formId: number;
    labels: {
        [property: string]: {
            [category: string]: string | null;
        } | null;
    };
}

export interface Labels {
    ACTIVITY: {
        academicFocusArea: {
            TEACHING: string;
            RESEARCH: string;
            ENGAGEMENT: string;
            SENIOR: string;
            PROFESSIONAL: string;
        };
    };
    PERSONAL_GOAL: {
        goalType: {
            CAREER_GOAL: string;
            MID_LONG_TERM_GOAL: string;
            CAREER_PROGRESSION: string;
            EQUITY_CONSIDERATION: string;
            CAPABILITY_DEVELOPMENT: string;
            TEACHING_AND_EDUCATIONAL_FACILITATION: string;
            RESEARCH_AND_SCHOLARSHIP: string;
            SERVICE_AND_ENGAGEMENT: string;
            LEADERSHIP: string;
        };
    };
    LEAVE: {
        absenceType: {
            annualLeave: string;
            pep: string;
            parentalLeave: string;
            longServiceLeave: string;
            outsideWork: string;
            secondmentsAndExchanges: string;
            personalLeave: string;
            extendedSickLeave: string;
            carersLeave: string;
            absenceFromDuty: string;
            leaveWithoutPay: string;
            communityLeave: string;
            domesticViolenceLeave: string;
            other: string;
        };
        leaveCode: {
            annualLeave: string;
            communityLeave: string;
            leaveWithoutPay: string;
            longServiceLeave: string;
            personalLeave: string;
            sickLeave: string;
            sickLeaveWithoutPay: string;
            studyLeave: string;
            parentalLeave: string;
        };
    };
    CONSIDERATION: {
        considerationType: {
            PEP: string;
            SUBJECT_TO_OPPORTUNITY: string;
            OUTSIDE_WORK: string;
            CONFLICTS_OF_INTEREST_DECLARATION: string;
            OTHER: string;
        };
    };
    BEHAVIOURAL: {
        behaviouralKpi: string | null;
    };
}

const GenericLabels = {
    ACTIVITY: {
        academicFocusArea: {
            TEACHING: 'Teaching and Learning',
            RESEARCH: 'Research',
            ENGAGEMENT: 'Service and Engagement',
            SENIOR: 'Senior Staff',
            PROFESSIONAL: 'Professional',
        },
    },
    PERSONAL_GOAL: {
        goalType: {
            CAREER_GOAL: 'Career Goal',
            MID_LONG_TERM_GOAL: 'Medium-Long Term Goal',
            CAREER_PROGRESSION: 'Career Progression',
            EQUITY_CONSIDERATION: 'Equity Consideration',
            CAPABILITY_DEVELOPMENT: 'Capability Development',
            TEACHING_AND_EDUCATIONAL_FACILITATION: 'Teaching & Educational Facilitation',
            RESEARCH_AND_SCHOLARSHIP: 'Research and Scholarship',
            SERVICE_AND_ENGAGEMENT: 'Service and Engagement',
            LEADERSHIP: 'Leadership',
            GENERAL: 'General (Recommended for Professional And Senior Staff)',
        },
    },
    LEAVE: {
        absenceType: {
            annualLeave: 'Annual Leave',
            pep: 'Professional Experience Program',
            parentalLeave: 'Parental Leave',
            longServiceLeave: 'Long Service Leave',
            outsideWork: 'Outside Work',
            secondmentsAndExchanges: 'Secondments and exchanges',
            personalLeave: 'Personal Leave',
            extendedSickLeave: 'Extended Sick Leave',
            carersLeave: 'Carers Leave',
            absenceFromDuty: 'Absence From Duty',
            leaveWithoutPay: 'Leave Without Pay',
            communityLeave: 'Community Leave',
            domesticViolenceLeave: 'Domestic Violence Leave',
            other: 'Other',
        },
        leaveCode: {
            annualLeave: 'Annual Leave',
            communityLeave: 'Community Leave',
            leaveWithoutPay: 'Leave Without Pay',
            longServiceLeave: 'Long Service Leave',
            personalLeave: 'Personal leave - UTS Only',
            sickLeave: 'Sick Leave',
            sickLeaveWithoutPay: 'Sick Leave Without Pay',
            studyLeave: 'Study Leave',
            parentalLeave: 'Parental Leave',
        },
    },
    CONSIDERATION: {
        considerationType: {
            PEP: 'PEP Planning',
            SUBJECT_TO_OPPORTUNITY: 'Subject to opportunity',
            OUTSIDE_WORK: 'Outside work',
            CONFLICTS_OF_INTEREST_DECLARATION: 'Conflicts of Interest Declaration',
            OTHER: 'Other',
        },
    },
    BEHAVIOURAL: {
        behaviouralKpi: null,
    },
};

export type LabelFormType = keyof typeof GenericLabels;

interface LabelsStore {
    generic: Labels;
    faculty: {
        [facultyId: string]: {
            [form: string]: number;
        };
    };
    forms: {
        [formId: string]: FormLabelsDto['labels'];
    };
}

interface ILabelsContext {
    getLabelsForFormId(formId: number | undefined, type: LabelFormType): FormLabelsDto['labels'];
    getGenericLabels(): typeof GenericLabels;
    store: LabelsStore;
}

export const LabelsContext = createUnprovisionedContext<ILabelsContext>('LabelsContext');

export const LabelsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [value, setValue] = useState<LabelsStore>({
        generic: GenericLabels,
        faculty: {},
        forms: {},
    });

    const { axiosInstance } = useAxios();

    const saveLabelsResponse = (labelResponse: FormLabelsDto, type: LabelFormType) => {
        let labels = { ...GenericLabels[type] };
        if (labelResponse) {
            labels = {
                ...labels,
                ...labelResponse.labels,
            };
        }

        setValue((oldValue) => set(cloneDeep(oldValue), `forms.${labelResponse?.formId}`, labels));
    };

    const fetchLabelsForFormId = useCallback(async (formId: number, type: LabelFormType) => {
        const properties = GenericLabels[type];
        const response = await axiosInstance.get<FormLabelsDto>(`/forms/labels/${formId}`, {
            params: {
                properties: Object.keys(properties),
            },
            validateStatus: () => true,
        });

        if (response.status === 200) {
            saveLabelsResponse(response.data, type);
        } else {
            console.error(`Failed retrieving labels for form id ${formId}, code ${response.status}`);
        }
    }, []);

    const getLabelsForFormId = useCallback(
        (formId: number | undefined, type: LabelFormType): FormLabelsDto['labels'] => {
            if (isNullish(formId)) return GenericLabels[type];

            const formLabels = value.forms[formId];
            if (!formLabels) {
                void fetchLabelsForFormId(formId, type);
                return GenericLabels[type];
            }
            return formLabels;
        },
        [value],
    );

    const ctx = useMemo(
        (): ILabelsContext => ({
            store: value,
            getLabelsForFormId,
            getGenericLabels: () => GenericLabels,
        }),
        [value, getLabelsForFormId],
    );

    return <LabelsContext.Provider value={ctx}>{children}</LabelsContext.Provider>;
};
